import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors, spacing, typography } from '../styles/theme';
import EventFinderAPI from '../services/api';
import type { Event, ResolvedEntity } from '../types/api';
import { SEO } from '../components/SEO';
import { SITE_URL, canonicalUrl, truncateDescription } from '../utils/seo';
import EventsExplorer from '../components/events/EventsExplorer';
import {
  RECENT_PAST_DAYS,
  RECENTLY_ADDED_DAYS,
  groupEventsByDuration,
  groupEventsByRecency,
} from '../utils/eventPresentation';
import config from '../config';
import EntityAvatar from '../components/EntityAvatar';
import { displayEntityType } from '../utils/entityTerminology';

/**
 * Canonical entity events page: /<entity-type>/<entity-slug>/events
 *
 * Resolves the slug to an entity id, redirects outdated/alias slugs to the
 * canonical URL, then loads that entity's whole event set in one request
 * (there is no pagination in this release).
 */

const Breadcrumbs = styled.nav`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.tertiary};
  margin-bottom: ${spacing[3]};

  a {
    color: ${colors.text.link};
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    text-decoration: underline;
  }
`;

export const EntityEventsPage: React.FC = () => {
  const { entityType = '', entitySlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [entity, setEntity] = useState<ResolvedEntity | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // Resolve the slug, then load events. A shared AbortController means
  // switching entities mid-flight can never paint the previous entity's data.
  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setNotFound(false);
    setEvents([]);
    setTotalAvailable(0);
    setEntity(null);

    (async () => {
      try {
        const resolution = await EventFinderAPI.resolveEntity(
          { entityType, slug: entitySlug },
          controller.signal,
        );
        if (cancelled) return;

        if (!resolution) {
          setNotFound(true);
          setIsLoading(false);
          return;
        }

        if (resolution.redirectTo && resolution.redirectTo !== window.location.pathname) {
          navigate({ pathname: resolution.redirectTo, search: searchParams.toString() }, { replace: true });
          return;
        }

        setEntity(resolution.entity);

        const response = await EventFinderAPI.getEntityEvents(resolution.entity.entityId, {
          limit: config.pagination.entityEventsLimit,
          recentPastDays: RECENT_PAST_DAYS,
          recentlyAddedDays: RECENTLY_ADDED_DAYS,
          signal: controller.signal,
        });
        if (cancelled) return;

        setEvents(Array.isArray(response.events) ? response.events : []);
        setTotalAvailable(response.pagination?.totalItems ?? 0);
        setIsLoading(false);
      } catch (err) {
        if (cancelled || (err as Error)?.name === 'AbortError') return;
        console.error('Failed to load entity events', err);
        setError(`We could not load these events. ${(err as Error)?.message || ''}`.trim());
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // Filters live in the URL and are applied client-side, so `searchParams`
    // must not retrigger a fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entitySlug, reloadToken, navigate]);

  const handleRetry = useCallback(() => setReloadToken((token) => token + 1), []);

  if (!entityType || !entitySlug) {
    return <Navigate to="/" replace />;
  }

  const { upcoming: currentEvents } = groupEventsByRecency(events);
  const { upcoming, ongoing } = groupEventsByDuration(
    currentEvents,
    config.eventDisplay.ongoingDurationHours,
  );
  const currentEventCount = upcoming.length + ongoing.length;
  const displayName = entity?.displayName ?? '';
  const entityTypeLabel = displayEntityType(entity?.entityType);
  const title = entity ? `${displayName} Events` : 'Events';
  const canonicalPath = entity?.canonicalUrl || `/${entityType}/${entitySlug}/events`;
  const description = entity
    ? truncateDescription(
        `${upcoming.length} upcoming and ${ongoing.length} ongoing ${currentEventCount === 1 ? 'event' : 'events'} from ${displayName}. Browse dates, venues, formats, and registration links.`,
      )
    : undefined;

  const breadcrumbData = entity
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          '@id': `${canonicalUrl(canonicalPath)}#collection`,
          name: title,
          description,
          url: canonicalUrl(canonicalPath),
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            name: 'Event Finder',
            alternateName: 'EventFinder',
            url: SITE_URL,
          },
          about: {
            '@type': 'Organization',
            name: displayName,
            url: canonicalUrl(canonicalPath),
            ...(entity.logoUrl ? { logo: canonicalUrl(entity.logoUrl) } : {}),
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: entityTypeLabel, item: canonicalUrl('/browse') },
            { '@type': 'ListItem', position: 3, name: displayName, item: canonicalUrl(canonicalPath) },
          ],
        },
      ]
    : [];

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonicalUrl(canonicalPath)}
        image={entity?.logoUrl || undefined}
        imageAlt={entity ? `${displayName} logo` : undefined}
        imageWidth={entity?.logoUrl ? 96 : undefined}
        imageHeight={entity?.logoUrl ? 96 : undefined}
        structuredData={breadcrumbData}
        noIndex={notFound}
      />
      <EventsExplorer
        title={title}
        titleIcon={entity ? (
          <EntityAvatar
            name={displayName}
            entityType={entity.entityType}
            logoUrl={entity.logoUrl}
            size={48}
            lazy={false}
          />
        ) : undefined}
        subtitle={
          entity
            ? `${entityTypeLabel} · ${upcoming.length} upcoming · ${ongoing.length} ongoing`
            : undefined
        }
        breadcrumbs={
          <Breadcrumbs aria-label="Breadcrumb">
            <a href="/">Home</a>
            {entity && (
              <>
                {' / '}
                <span>{entityTypeLabel}</span>
                {' / '}
                <span aria-current="page">{displayName}</span>
              </>
            )}
          </Breadcrumbs>
        }
        events={events}
        isLoading={isLoading}
        error={notFound ? `We could not find an entity at /${entityType}/${entitySlug}/events.` : error}
        onRetry={notFound ? undefined : handleRetry}
        totalAvailable={totalAvailable}
        fetchLimit={config.pagination.entityEventsLimit}
        exportLabel={displayName || entitySlug}
        contextEntityId={entity?.entityId ?? null}
        emptyMessage="There are no upcoming or recent events for this entity yet."
        telemetryCategory="entity_events"
        showRecentlyAdded
        showDurationTabs
      />
    </>
  );
};

export default EntityEventsPage;
